/**
 * Schema migration.
 *
 * Given `(oldSchema, newSchema, data)`, produce `(migratedData, report)`
 * where migratedData validates structurally against newSchema as much as
 * possible and report tells the user what changed.
 *
 * Scope (v1): safe automatic transforms only.
 *   - type unchanged  -> keep value
 *   - type changed    -> drop, reset to default, record in report
 *   - field removed   -> drop, record
 *   - field added     -> fill from default if required, else omit
 *   - enum narrowed   -> invalid value dropped
 *   - oneOf variant disappeared -> drop that item, reset to first variant default
 *   - array items schema changed -> migrate each element independently
 *
 * Out of scope (throws or reports as UNSUPPORTED):
 *   - field renames (we'd have to guess; renames should ship as explicit
 *     migration steps in a future MigrationScript API)
 *   - value transforms across types (string "42" -> number 42, etc.)
 *
 * The report is structured so the UI can render a dry-run preview. The
 * migrator NEVER throws on migration-time decisions — it records and moves
 * on. It only throws on programmer errors (e.g. bad $ref that would have
 * thrown anyway).
 */

import type { JsonSchema, JsonValue } from '@bliksys/shared';

import { defaultValueFor } from './defaults.js';
import { buildPointer } from './pointer.js';
import { resolveSchema } from './resolver.js';

export type MigrationChangeKind =
  | 'kept'
  | 'dropped-removed-field'
  | 'dropped-type-mismatch'
  | 'dropped-invalid-enum'
  | 'dropped-invalid-variant'
  | 'reset-to-default'
  | 'added-required-default'
  | 'array-item-dropped';

export interface MigrationChange {
  /** JSON pointer to the site of the change in the OLD data. */
  path: string;
  kind: MigrationChangeKind;
  /** Human message for the UI to display verbatim. */
  message: string;
}

export interface MigrationResult {
  data: JsonValue;
  changes: readonly MigrationChange[];
  /** Breakdown useful for the UI summary row. */
  summary: {
    kept: number;
    dropped: number;
    defaulted: number;
  };
}

export function migrate(
  oldSchema: JsonSchema,
  newSchema: JsonSchema,
  data: JsonValue,
): MigrationResult {
  const changes: MigrationChange[] = [];
  const migrated = migrateNode(data, oldSchema, newSchema, newSchema, '', changes);
  const summary = summarize(changes);
  return { data: migrated, changes, summary };
}

function migrateNode(
  value: JsonValue | undefined,
  oldSchema: JsonSchema,
  newSchema: JsonSchema,
  newRoot: JsonSchema,
  path: string,
  changes: MigrationChange[],
): JsonValue {
  const resolvedNew = resolveSchema(newSchema, newRoot);

  // oneOf: pick the variant that matches, migrate against it.
  const variants = resolvedNew.oneOf ?? resolvedNew.anyOf;
  if (variants && variants.length > 0) {
    return migrateOneOf(value, oldSchema, resolvedNew, variants, newRoot, path, changes);
  }

  const newType = normalizeType(resolvedNew);

  if (newType === 'object') {
    return migrateObject(value, oldSchema, resolvedNew, newRoot, path, changes);
  }
  if (newType === 'array') {
    return migrateArray(value, oldSchema, resolvedNew, newRoot, path, changes);
  }

  // Leaf. Keep if type compatible, else drop to default.
  return migrateLeaf(value, resolvedNew, newRoot, path, changes);
}

function migrateObject(
  value: JsonValue | undefined,
  _oldSchema: JsonSchema,
  newSchema: JsonSchema,
  newRoot: JsonSchema,
  path: string,
  changes: MigrationChange[],
): JsonValue {
  const obj = isPlainObject(value) ? value : {};
  const result: Record<string, JsonValue> = {};
  const newProps = newSchema.properties ?? {};
  const required = new Set(newSchema.required ?? []);
  const knownKeys = new Set(Object.keys(newProps));

  // Carry over every key that still exists in the new schema.
  for (const [key, newPropSchema] of Object.entries(newProps)) {
    const childPath = buildChildPath(path, key);
    const oldValue = obj[key];
    if (oldValue === undefined) {
      if (required.has(key)) {
        result[key] = defaultValueFor(newPropSchema, newRoot);
        changes.push({
          path: childPath,
          kind: 'added-required-default',
          message: `Added required field "${key}" with default value.`,
        });
      }
      continue;
    }
    // Recurse. We don't know the old subschema precisely, but for migration
    // purposes we only care about new vs current value, so pass newPropSchema
    // on both sides. If you want cross-schema rename inference, that's v2.
    result[key] = migrateNode(oldValue, newPropSchema, newPropSchema, newRoot, childPath, changes);
  }

  // Report keys that existed in data but aren't in the new schema.
  for (const key of Object.keys(obj)) {
    if (knownKeys.has(key)) continue;
    changes.push({
      path: buildChildPath(path, key),
      kind: 'dropped-removed-field',
      message: `Dropped field "${key}" — not present in the new schema.`,
    });
  }

  return result;
}

function migrateArray(
  value: JsonValue | undefined,
  _oldSchema: JsonSchema,
  newSchema: JsonSchema,
  newRoot: JsonSchema,
  path: string,
  changes: MigrationChange[],
): JsonValue {
  if (!Array.isArray(value)) {
    // Type shifted to array; reset.
    if (value !== undefined && value !== null) {
      changes.push({
        path,
        kind: 'dropped-type-mismatch',
        message: `Field is now an array; previous value reset.`,
      });
    }
    return [];
  }
  const itemSchema = newSchema.items;
  if (!itemSchema) return value; // No item schema to migrate against.

  const result: JsonValue[] = [];
  for (let i = 0; i < value.length; i++) {
    const childPath = buildChildPath(path, String(i));
    const item = value[i];
    if (item === undefined) continue;
    // Check whether the item can survive. For oneOf items this is where
    // variants that disappeared get dropped.
    const migrated = migrateNode(item, itemSchema, itemSchema, newRoot, childPath, changes);
    if (migrated === DROP_MARKER) {
      changes.push({
        path: childPath,
        kind: 'array-item-dropped',
        message: `Removed array item — no longer fits the new schema.`,
      });
      continue;
    }
    result.push(migrated);
  }
  return result;
}

/**
 * Sentinel: item-level "drop me" signal used by oneOf migration to prune
 * orphaned variants inside arrays. Not exported — treated as plain JSON
 * where it escapes the array migrator (shouldn't happen).
 */
const DROP_MARKER = Symbol('bliksys.migration.drop') as unknown as JsonValue;

function migrateOneOf(
  value: JsonValue | undefined,
  _oldSchema: JsonSchema,
  newSchema: JsonSchema,
  variants: JsonSchema[],
  newRoot: JsonSchema,
  path: string,
  changes: MigrationChange[],
): JsonValue {
  const discProp = newSchema.discriminator?.propertyName;
  const discValue = discProp && isPlainObject(value) ? value[discProp] : undefined;

  // Resolve each variant.
  const resolvedVariants = variants.map((v) => resolveSchema(v, newRoot));

  if (discProp && typeof discValue === 'string') {
    const match = resolvedVariants.find((v) => readConstProperty(v, discProp) === discValue);
    if (match) {
      // Migrate against the surviving variant.
      return migrateNode(value, match, match, newRoot, path, changes);
    }
    // The variant this data claimed is no longer in the union.
    changes.push({
      path,
      kind: 'dropped-invalid-variant',
      message: `Variant "${discValue}" is no longer allowed; item removed.`,
    });
    return DROP_MARKER;
  }

  // No discriminator or no data: fall back to first variant's default.
  if (value === undefined) {
    return defaultValueFor(resolvedVariants[0]!, newRoot);
  }
  return value;
}

function migrateLeaf(
  value: JsonValue | undefined,
  newSchema: JsonSchema,
  newRoot: JsonSchema,
  path: string,
  changes: MigrationChange[],
): JsonValue {
  if (value === undefined) return defaultValueFor(newSchema, newRoot);
  const newType = normalizeType(newSchema);

  // Enum narrowing: drop if value no longer allowed.
  if (newSchema.enum && newSchema.enum.length > 0) {
    if (newSchema.enum.some((e) => jsonEqual(e, value))) return value;
    changes.push({
      path,
      kind: 'dropped-invalid-enum',
      message: `Value is no longer an allowed option; reset to default.`,
    });
    return defaultValueFor(newSchema, newRoot);
  }

  const valueType = jsTypeOf(value);
  if (newType === undefined || valueType === newType) return value;
  if (newType === 'integer' && valueType === 'number' && Number.isInteger(value)) {
    return value;
  }
  changes.push({
    path,
    kind: 'dropped-type-mismatch',
    message: `Expected ${String(newType)}; dropped incompatible value.`,
  });
  return defaultValueFor(newSchema, newRoot);
}

/* ---------------- helpers ---------------- */

function normalizeType(schema: JsonSchema): JsonSchema['type'] | undefined {
  if (Array.isArray(schema.type)) {
    const nonNull = schema.type.filter((t) => t !== 'null');
    return nonNull.length === 1 ? nonNull[0] : undefined;
  }
  if (schema.type === undefined) {
    if (schema.properties) return 'object';
    if (schema.items) return 'array';
    return undefined;
  }
  return schema.type;
}

function jsTypeOf(value: JsonValue): JsonSchema['type'] | undefined {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return Number.isInteger(value) ? 'integer' : 'number';
  if (typeof value === 'string') return 'string';
  return undefined;
}

function isPlainObject(value: JsonValue | undefined): value is Record<string, JsonValue> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function readConstProperty(schema: JsonSchema, propertyName: string): string | undefined {
  const prop = schema.properties?.[propertyName];
  if (!prop) return undefined;
  if (typeof prop.const === 'string') return prop.const;
  if (prop.enum?.length === 1 && typeof prop.enum[0] === 'string') return prop.enum[0];
  return undefined;
}

function buildChildPath(parent: string, segment: string): string {
  return buildPointer([
    ...(parent === '' ? [] : parent.slice(1).split('/').map(unescape)),
    segment,
  ]);
}

function unescape(seg: string): string {
  return seg.replace(/~1/g, '/').replace(/~0/g, '~');
}

function jsonEqual(a: JsonValue, b: JsonValue): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => jsonEqual(v, b[i]!));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ak = Object.keys(a);
    const bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    return ak.every((k) =>
      jsonEqual((a as Record<string, JsonValue>)[k]!, (b as Record<string, JsonValue>)[k]!),
    );
  }
  return false;
}

function summarize(changes: readonly MigrationChange[]): MigrationResult['summary'] {
  let kept = 0;
  let dropped = 0;
  let defaulted = 0;
  for (const c of changes) {
    switch (c.kind) {
      case 'kept':
        kept++;
        break;
      case 'dropped-removed-field':
      case 'dropped-type-mismatch':
      case 'dropped-invalid-enum':
      case 'dropped-invalid-variant':
      case 'array-item-dropped':
        dropped++;
        break;
      case 'reset-to-default':
      case 'added-required-default':
        defaulted++;
        break;
    }
  }
  return { kept, dropped, defaulted };
}
